import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card, Alert, Switch } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';

/**
 * 🔔 Modal de Preferências de Notificações
 * 
 * Permite ao usuário configurar quais tipos de notificações
 * deseja receber e como deseja recebê-las.
 */
const NotificationPreferencesModal = ({ show, onHide }) => {
  const [preferences, setPreferences] = useState({
    // Alta prioridade (alertas)
    ticket_replied: { enabled: true, alerts: true, email: false },
    affiliate_request: { enabled: true, alerts: true, email: false },
    withdrawal_approved: { enabled: true, alerts: true, email: false },
    withdrawal_rejected: { enabled: true, alerts: true, email: false },
    
    // Baixa prioridade (apenas dropdown)
    sale_new: { enabled: true, alerts: false, email: false },
    commission_received: { enabled: true, alerts: false, email: false },
    balance_available: { enabled: true, alerts: false, email: false }
  });
  
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // Carregar preferências do backend
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Backend retorna { success: true, preferences: {...} }
        if (data.success && data.preferences) {
          // Backend retorna formato simples: { email: true, push: true, ... }
          // Converter para formato do frontend: { ticket_replied: { enabled: true, alerts: true, email: false }, ... }
          // Por enquanto, usar preferências padrão até implementar conversão
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Preferências carregadas do backend:', data.preferences);
          }
        }
      } else {
        console.error('Erro ao carregar preferências do servidor');
        // Fallback para localStorage
        const savedPrefs = localStorage.getItem('notification_preferences');
        if (savedPrefs) {
          setPreferences(JSON.parse(savedPrefs));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar preferências:', error);
      // Fallback para localStorage
      const savedPrefs = localStorage.getItem('notification_preferences');
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  };

  // Salvar preferências
  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });
      
      if (response.ok) {
        // Salvar também no localStorage como backup
        localStorage.setItem('notification_preferences', JSON.stringify(preferences));
        
        setSaved(true);
        toast.success('Preferências salvas com sucesso!');
        
        setTimeout(() => {
          setSaved(false);
          onHide();
        }, 1500);
      } else {
        throw new Error('Erro ao salvar no servidor');
      }
      
    } catch (error) {
      console.error('Erro ao salvar preferências:', error);
      toast.error('Erro ao salvar preferências');
    } finally {
      setLoading(false);
    }
  };

  // Atualizar preferência
  const updatePreference = (type, field, value) => {
    setPreferences(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  // Toggle geral
  const toggleAll = (enabled) => {
    const newPrefs = {};
    Object.keys(preferences).forEach(key => {
      newPrefs[key] = {
        ...preferences[key],
        enabled
      };
    });
    setPreferences(newPrefs);
  };

  const highPriorityTypes = [
    { key: 'ticket_replied', label: 'Tickets Respondidos' },
    { key: 'affiliate_request', label: 'Solicitações de Afiliação' },
    { key: 'withdrawal_approved', label: 'Saques Aprovados' },
    { key: 'withdrawal_rejected', label: 'Saques Rejeitados' }
  ];

  const lowPriorityTypes = [
    { key: 'sale_new', label: 'Novas Vendas' },
    { key: 'commission_received', label: 'Comissões Recebidas' },
    { key: 'balance_available', label: 'Saldo Disponível' }
  ];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="d-flex align-items-center">
          <Icon icon="mdi:bell-cog" className="me-2 text-primary" style={{ fontSize: '1.5rem' }} />
          <h4 className="mb-0">Preferências de Notificações</h4>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {saved && (
          <Alert variant="success" className="mb-4">
            <Icon icon="mdi:check-circle" className="me-2" />
            Preferências salvas com sucesso!
          </Alert>
        )}
        
        {/* Botões de controle */}
        <div className="mb-5 d-flex justify-content-end">
          <Button 
            variant="outline-success" 
            size="sm" 
            onClick={() => toggleAll(true)}
            className="me-2"
          >
            <Icon icon="mdi:check-all" className="me-1" />
            Ativar Todas
          </Button>
          <Button 
            variant="outline-danger" 
            size="sm" 
            onClick={() => toggleAll(false)}
          >
            <Icon icon="mdi:close-all" className="me-1" />
            Desativar Todas
          </Button>
        </div>

        {/* Layout em 2 colunas: Alta e Baixa Prioridade */}
        <Row className="g-4">
          {/* Coluna Alta Prioridade */}
          <Col md={6}>
            <h6 className="mb-3">
              <Icon icon="mdi:alert-circle" className="me-2 text-warning" />
              Alta Prioridade <small className="text-muted">(Alertas)</small>
            </h6>
            
            <div className="d-flex flex-column gap-3">
              {highPriorityTypes.map(type => (
                <Card key={type.key}>
                  <Card.Body className="py-2 px-3">
                    {/* Título principal com switch */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>{type.label}</span>
                      <Form.Check
                        type="switch"
                        id={`${type.key}_enabled`}
                        checked={preferences[type.key]?.enabled || false}
                        onChange={(e) => updatePreference(type.key, 'enabled', e.target.checked)}
                      />
                    </div>
                    
                    {/* Opções de alertas e email */}
                    {preferences[type.key]?.enabled && (
                      <div className="border-top pt-2 mt-1">
                        <div className="mb-1">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <Icon icon="mdi:bell-ring" className="me-2 text-primary" style={{ fontSize: '0.85rem' }} />
                              <span style={{ fontSize: '0.85rem' }}>Alertas</span>
                            </div>
                            <Form.Check
                              type="switch"
                              id={`${type.key}_alerts`}
                              checked={preferences[type.key]?.alerts || false}
                              onChange={(e) => updatePreference(type.key, 'alerts', e.target.checked)}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <Icon icon="mdi:email" className="me-2 text-info" style={{ fontSize: '0.85rem' }} />
                              <span style={{ fontSize: '0.85rem' }}>Email</span>
                            </div>
                            <Form.Check
                              type="switch"
                              id={`${type.key}_email`}
                              checked={preferences[type.key]?.email || false}
                              onChange={(e) => updatePreference(type.key, 'email', e.target.checked)}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Col>

          {/* Coluna Baixa Prioridade */}
          <Col md={6}>
            <h6 className="mb-3">
              <Icon icon="mdi:information" className="me-2 text-info" />
              Baixa Prioridade <small className="text-muted">(Dropdown)</small>
            </h6>
            
            <div className="d-flex flex-column gap-3">
              {lowPriorityTypes.map(type => (
                <Card key={type.key}>
                  <Card.Body className="py-2 px-3">
                    {/* Título principal com switch */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>{type.label}</span>
                      <Form.Check
                        type="switch"
                        id={`${type.key}_enabled`}
                        checked={preferences[type.key]?.enabled || false}
                        onChange={(e) => updatePreference(type.key, 'enabled', e.target.checked)}
                      />
                    </div>
                    
                    {/* Opções de alertas e email */}
                    {preferences[type.key]?.enabled && (
                      <div className="border-top pt-2 mt-1">
                        <div className="mb-1">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <Icon icon="mdi:bell-ring" className="me-2 text-primary" style={{ fontSize: '0.85rem' }} />
                              <span style={{ fontSize: '0.85rem' }}>Alertas</span>
                            </div>
                            <Form.Check
                              type="switch"
                              id={`${type.key}_alerts`}
                              checked={preferences[type.key]?.alerts || false}
                              onChange={(e) => updatePreference(type.key, 'alerts', e.target.checked)}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <Icon icon="mdi:email" className="me-2 text-info" style={{ fontSize: '0.85rem' }} />
                              <span style={{ fontSize: '0.85rem' }}>Email</span>
                            </div>
                            <Form.Check
                              type="switch"
                              id={`${type.key}_email`}
                              checked={preferences[type.key]?.email || false}
                              onChange={(e) => updatePreference(type.key, 'email', e.target.checked)}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Col>
        </Row>

        {/* Informações */}
        <Alert variant="info" className="mt-5 mb-0">
          <div className="d-flex align-items-start">
            <Icon icon="mdi:information" className="me-3 mt-1" />
            <div>
              <strong>Como funciona:</strong>
              <ul className="mb-0 mt-2">
                <li><strong>Alertas:</strong> Notificações que aparecem na tela</li>
                <li><strong>Email:</strong> Notificações por email (em desenvolvimento)</li>
                <li><strong>Dropdown:</strong> Todas aparecem no ícone do sino</li>
              </ul>
            </div>
          </div>
        </Alert>
      </Modal.Body>
      
      <Modal.Footer className="border-0">
        <Button variant="outline-secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? (
            <>
              <Icon icon="mdi:loading" className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
              Salvando...
            </>
          ) : (
            <>
              <Icon icon="mdi:content-save" className="me-2" />
              Salvar Preferências
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NotificationPreferencesModal;
